import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel,
  Stack, TextField, Typography,
} from '@mui/material';
import { API_BASE_URL } from '../config';
import { captureAttribution } from '../utils/attribution';
import { fbTrack, eventIdForLead } from '../utils/fbPixel';

/**
 * Inline signup form used on the /start LP.
 *
 * Submits to the existing `/api/v1/users/register` endpoint — inherits the
 * daily signup limit enforced server-side. On HTTP 429, renders a friendly
 * "signups paused for today" message instead of a generic error.
 *
 * Props:
 *   - sessionId: the web_chat_session_id from ChatWidget; sent to the server
 *     so the onboarding state doc can capture the chat transcript.
 */
export default function InlineSignupForm({ sessionId }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [limitHit, setLimitHit] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!terms) {
      setError('Please agree to the terms to continue.');
      return;
    }
    if (!name.trim() || !email.trim() || !phone.trim() || !password) {
      setError('All fields are required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setLimitHit(false);

    try {
      // captureAttribution() also backfills _fbp/_fbc, which fbevents.js may
      // not have set yet at first capture — they matter for CAPI match quality.
      const attribution = captureAttribution();
      // The chat widget writes (session_id, session_token) to sessionStorage
      // as a PAIR every time it boots. We must read them together — reading
      // session_id from the parent prop and session_token from storage opens
      // a window where the prop reflects an older session than storage (or
      // vice versa); the backend hashes the token and rejects the transcript
      // when id/token disagree, and the welcome SMS lands without context.
      // If storage has the pair, prefer that. Fall back to the prop only if
      // storage is empty (defensive — shouldn't happen in practice).
      let webChatSessionId = null;
      let webChatSessionToken = null;
      try {
        webChatSessionId = sessionStorage.getItem('aven_web_chat_session_id');
        webChatSessionToken = sessionStorage.getItem('aven_web_chat_session_token');
      } catch (_) {}
      if (!webChatSessionId) {
        webChatSessionId = sessionId || null;
        webChatSessionToken = null;
      }
      const res = await axios.post(
        `${API_BASE_URL}/api/v1/users/register`,
        {
          user_in: {
            full_name: name.trim(),
            email: email.trim(),
            phone_number: phone.trim(),
            password,
            web_chat_session_id: webChatSessionId,
            web_chat_session_token: webChatSessionToken,
            attribution,
          },
        }
      );

      const data = res.data || {};
      // Store token so the /success page can read it if needed
      try {
        if (data.access_token) {
          localStorage.setItem('aven_token', data.access_token);
        }
      } catch (_) {}

      // Fire FB Pixel Lead — deterministic event_id dedupes with the
      // server-side CAPI Lead event fired by the registration endpoint.
      try {
        fbTrack('Lead', { content_name: 'onboarding_signup' }, eventIdForLead(data.id));
      } catch (_) {}

      navigate('/success?source=start');
    } catch (err) {
      const statusCode = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (statusCode === 429) {
        // Friendly path — NOT an error.
        setLimitHit(true);
      } else if (statusCode === 400 && typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Signup failed — try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (limitHit) {
    return (
      <Box sx={{ p: 3, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fafafa' }}>
        <Typography variant="h6" gutterBottom>
          Signups are paused for today
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We cap new signups daily so we can give every new user the attention they need.
          Check back tomorrow — we'll be here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        <TextField
          label="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoComplete="name"
          inputProps={{ 'aria-label': 'Your name' }}
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          inputProps={{ 'aria-label': 'Email' }}
        />
        <TextField
          label="Mobile number"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          autoComplete="tel"
          helperText="Aven will text this number — US numbers only."
          inputProps={{ 'aria-label': 'Mobile number' }}
        />
        <TextField
          label="Create a password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
          inputProps={{ 'aria-label': 'Password' }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              inputProps={{ 'aria-label': 'Agree to terms and privacy policy' }}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>,
              and consent to receive SMS messages from Aven.
            </Typography>
          }
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={submitting}
          sx={{ py: 1.3 }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Text me'}
        </Button>
      </Stack>
    </Box>
  );
}
